import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  IconButton,
  ListItem,
  ListItemText,
  Collapse,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import LogoutIcon from '@mui/icons-material/Logout';
import { useTheme } from '@mui/material/styles';
import {
  CustomListItemButton,
  CustomListItemIcon,
  Drawer,
  DrawerHeader,
} from './Style/Sidebar.style';
import { SidebarProps } from '../../helpers/types';
import { menuItems } from '../../utils';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import KeyboardShortcutsHelp from '../ThreatTable/KeyboardShortcutsHelp';
import { getShortcutsForRoute } from '../../hooks/keyboardShortcuts.constants';
import { useAuth0 } from '../../__mocks__/@auth0/auth0-react';

const Sidebar = ({
  handleDrawerClose,
  open,
  handleDrawerOpen,
}: SidebarProps) => {
  const location = useLocation();
  const theme = useTheme();
  const { logout } = useAuth0();
  const [openSubmenus, setOpenSubmenus] = useState<Record<number, boolean>>({});
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);

  const handleLogout = () => {
    localStorage.clear();
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  // Feature-flag gated menu items. Add a hook here for each flag we gate on.
  const threatExplainStats = useFeatureFlag('threat-explain-stats');
  // Memoized so its identity is stable across renders — it feeds the
  // submenu auto-expand effect's dependency array below
  const visibleMenuItems = useMemo(() => {
    const flagValues: Record<string, boolean> = {
      'threat-explain-stats': threatExplainStats,
    };
    return menuItems.filter(item => !item.flag || flagValues[item.flag]);
  }, [threatExplainStats]);

  const toggleSubmenu = (id: number) => {
    setOpenSubmenus(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Modified: added to check if any submenu item is active and open the parent menu
  useEffect(() => {
    const newOpenSubmenus: Record<number, boolean> = {};

    visibleMenuItems.forEach(item => {
      if (item.submenu && item.submenu.length > 0) {
        const hasActiveSubmenu = item.submenu.some(subitem => {
          // Special handling for Registrars submenu
          if (subitem.id === 112 && subitem.menuName === 'Registrars') {
            return location.pathname.startsWith('/settings/registrars');
          }
          return (
            location.pathname === subitem.to ||
            location.pathname.startsWith(subitem.to + '/')
          );
        });

        if (hasActiveSubmenu) {
          newOpenSubmenus[item.id] = true;
        }
      }
    });

    // Merge with existing state to preserve manually opened menus
    setOpenSubmenus(prev => ({
      ...prev,
      ...newOpenSubmenus,
    }));
  }, [location.pathname, visibleMenuItems]);

  const closeSubmenu = (id: number) => {
    setOpenSubmenus(prev => ({
      ...prev,
      [id]: false,
    }));
  };

  return (
    <Drawer variant="permanent" open={open}>
      <DrawerHeader>
        <IconButton
          onClick={open ? handleDrawerClose : handleDrawerOpen}
          size="small"
          sx={{ mx: 'auto' }}
        >
          {!open ? (
            <ChevronRightIcon fontSize="small" />
          ) : (
            <ChevronLeftIcon fontSize="small" />
          )}
        </IconButton>
        {open && (
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ flexGrow: 1, textAlign: 'center' }}
          >
            Wheel
          </Typography>
        )}
      </DrawerHeader>
      <List dense disablePadding>
        {visibleMenuItems.map(item => {
          // Special handling for Settings menu: check if path starts with /settings
          let isActive = location.pathname.startsWith(item.to);
          if (item.id === 11 && item.menuName === 'Settings') {
            isActive = location.pathname.startsWith('/settings');
          }
          const isSubmenuOpen = openSubmenus[item.id];

          return (
            <div key={item.id}>
              <Link
                to={item.to}
                style={{
                  textDecoration: 'none',
                  color: theme.palette.text.primary,
                }}
              >
                <ListItem disablePadding>
                  <CustomListItemButton isActive={isActive} open={open}>
                    <CustomListItemIcon isActive={isActive}>
                      {React.cloneElement(item.icon as React.ReactElement, {
                        sx: { fontSize: 20 },
                      })}
                    </CustomListItemIcon>
                    <ListItemText
                      primary={item.menuName}
                      primaryTypographyProps={{ fontSize: 13, lineHeight: 1.3 }}
                      sx={{
                        opacity: open ? 1 : 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    />
                    {item.submenu && open && (
                      <IconButton
                        onClick={e => {
                          e.preventDefault();
                          if (isSubmenuOpen) {
                            closeSubmenu(item.id);
                          } else {
                            toggleSubmenu(item.id);
                          }
                        }}
                        size="small"
                        sx={{ padding: '2px' }}
                      >
                        {isSubmenuOpen ? (
                          <ExpandLess sx={{ fontSize: 16 }} />
                        ) : (
                          <ExpandMore sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    )}
                  </CustomListItemButton>
                </ListItem>
              </Link>
              {item.submenu?.length && (
                <Collapse in={isSubmenuOpen} timeout="auto" unmountOnExit>
                  <List component="div" dense disablePadding>
                    {item.submenu.map(subitem => {
                      // Special handling for Registrars submenu: check if path starts with /settings/registrars
                      let isSubitemActive =
                        location.pathname === subitem.to ||
                        location.pathname.startsWith(subitem.to + '/');
                      if (
                        subitem.id === 112 &&
                        subitem.menuName === 'Registrars'
                      ) {
                        isSubitemActive = location.pathname.startsWith(
                          '/settings/registrars',
                        );
                      }
                      return (
                        <Link
                          to={subitem.to}
                          key={subitem.id}
                          style={{
                            textDecoration: 'none',
                            color: theme.palette.text.primary,
                          }}
                        >
                          <ListItem disablePadding>
                            <CustomListItemButton
                              isActive={isSubitemActive}
                              open={open}
                              sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                marginLeft: open ? '24px' : '0',
                                paddingLeft: open ? '12px' : '0',
                              }}
                            >
                              <CustomListItemIcon isActive={isSubitemActive}>
                                <FiberManualRecordIcon
                                  sx={{
                                    fontSize: '6px',
                                    color: isSubitemActive
                                      ? theme.palette.primary.main
                                      : 'inherit',
                                  }}
                                />
                              </CustomListItemIcon>
                              <ListItemText
                                primary={subitem.menuName}
                                primaryTypographyProps={{
                                  fontSize: 12,
                                  lineHeight: 1.3,
                                }}
                                sx={{
                                  opacity: open ? 1 : 0,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              />
                            </CustomListItemButton>
                          </ListItem>
                        </Link>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </div>
          );
        })}
      </List>
      <Box sx={{ marginTop: 'auto' }}>
        <Box
          sx={{
            p: 1,
            display: 'flex',
            justifyContent: open ? 'flex-start' : 'center',
          }}
        >
          <Tooltip title="Keyboard shortcuts (?)" placement="right">
            <ListItem disablePadding>
              <CustomListItemButton
                isActive={false}
                open={open}
                onClick={() => setShowShortcutsHelp(true)}
              >
                <CustomListItemIcon isActive={false}>
                  <KeyboardIcon sx={{ fontSize: 20 }} />
                </CustomListItemIcon>
                <ListItemText
                  primary="Shortcuts"
                  primaryTypographyProps={{ fontSize: 13, lineHeight: 1.3 }}
                  sx={{
                    opacity: open ? 1 : 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                />
              </CustomListItemButton>
            </ListItem>
          </Tooltip>
        </Box>
        <Box
          sx={{
            p: 1,
            pt: 0,
            display: 'flex',
            justifyContent: open ? 'flex-start' : 'center',
          }}
        >
          <Tooltip title="Logout" placement="right">
            <ListItem disablePadding>
              <CustomListItemButton
                isActive={false}
                open={open}
                onClick={handleLogout}
                data-testid="sidebar-logout"
              >
                <CustomListItemIcon isActive={false}>
                  <LogoutIcon sx={{ fontSize: 20 }} />
                </CustomListItemIcon>
                <ListItemText
                  primary="Logout"
                  primaryTypographyProps={{ fontSize: 13, lineHeight: 1.3 }}
                  sx={{
                    opacity: open ? 1 : 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                />
              </CustomListItemButton>
            </ListItem>
          </Tooltip>
        </Box>
      </Box>
      <KeyboardShortcutsHelp
        open={showShortcutsHelp}
        onClose={() => setShowShortcutsHelp(false)}
        shortcuts={getShortcutsForRoute(location.pathname)}
      />
    </Drawer>
  );
};

export default Sidebar;
